import { gql } from '@apollo/client';
import moment from 'moment';
import React from 'react';
import Button from '../../components/Button';
import CallOut from '../../components/CallOut';
import { Cards } from '../../components/Card';
import MetaList, { MetaListItem } from '../../components/MetaList';
import type {
  BillingStatusView_BillingPricingFragment,
  BillingStatusView_BillingSummaryFragment,
  PlanInterval,
  PlanTier,
} from '../../graphql/generated/graphqlTypes';
import useSeatLimits from '../../invitation/useSeatLimits';
import SettingCard from '../../settings/SettingCard';
import SettingsSection from '../../settings/SettingsSection';
import UIActions from '../../ui/UIActions';
import formatCurrency from '../../utils/formatCurrency';
import AdminBillingNotice from './AdminBillingNotice';
import styles from './BillingStatusView.module.scss';
import EditEmailModal from './EditEmailModal';
import PaymentCard from './PaymentCard';
import Plan from './Plan';
import { useAppDispatch } from '../../app/redux/useAppSelector';

const BILLING_SUMMARY_FRAGMENT = gql`
  fragment BillingStatusView_billingSummary on BillingSummary {
    coupon {
      name
    }
    customer {
      email
      balance
    }
    periodEndAt
    ...PaymentCard_billingSummary
    ...AdminBillingNotice_billingSummary
    ...Plan_billingSummary
  }
  ${PaymentCard.fragments.billingSummary}
  ${AdminBillingNotice.fragments.billingSummary}
  ${Plan.fragments.billingSummary}
`;

const BILLING_PRICING_FRAGMENT = gql`
  fragment BillingStatusView_billingPricing on BillingPricing {
    free {
      monthly {
        amount
      }
      yearly {
        amount
      }
    }
    startup {
      monthly {
        amount
      }
      yearly {
        amount
      }
    }
    business {
      monthly {
        amount
      }
      yearly {
        amount
      }
    }
    ...Plan_billingPricing
  }
  ${Plan.fragments.billingPricing}
`;

interface BillingStatusViewProps {
  billingSummary: BillingStatusView_BillingSummaryFragment;
  billingPricing: BillingStatusView_BillingPricingFragment;
  updateBilling: (values: {
    email?: string;
    source?: string;
    planTier?: PlanTier;
    planInterval?: PlanInterval;
  }) => Promise<unknown>;
  cancelBilling: () => void;
  initialTier: PlanTier | null;
  initialInterval: PlanInterval | null;
}

const BillingStatusView = ({
  billingSummary,
  billingPricing,
  updateBilling,
  cancelBilling,
  initialTier,
  initialInterval,
}: BillingStatusViewProps) => {
  const dispatch = useAppDispatch();

  const { coupon, customer, periodEndAt } = billingSummary;
  const { email, balance } = customer;

  const { isSeatLimitMaxed } = useSeatLimits();

  const showEmailModal = () =>
    dispatch(UIActions.openModal(EditEmailModal, { updateBilling, email }));

  const credit = balance && balance < 0 ? -balance : null;

  return (
    <>
      {isSeatLimitMaxed && (
        <div className={styles.limitCalloutContainer}>
          <CallOut variant="danger">
            Your team has reached the seat limit for your plan. Please{' '}
            <a href="mailto:support@slab.com">contact support</a> to add
            additional users.
          </CallOut>
        </div>
      )}
      <SettingsSection header="Plan details">
        <div className={styles.noticeWrapper}>
          <AdminBillingNotice
            billingSummary={billingSummary}
            isTrialEnded={false}
          />
        </div>
        <Plan
          billingSummary={billingSummary}
          billingPricing={billingPricing}
          updateBilling={updateBilling}
          initialTier={initialTier}
          initialInterval={initialInterval}
        />
      </SettingsSection>

      <SettingsSection header="Payment method">
        <Cards>
          {!!credit && (
            <SettingCard
              title="Credits"
              details={`${formatCurrency(credit)} remaining in credit`}
            />
          )}
          <PaymentCard
            billingSummary={billingSummary}
            updateBilling={updateBilling}
          />
          {coupon && (
            <SettingCard
              title="Coupon"
              details={
                <MetaList>
                  <MetaListItem>{coupon.name}</MetaListItem>
                </MetaList>
              }
            />
          )}
        </Cards>
        <div className={styles.emailInfoContainer}>
          <MetaList>
            <MetaListItem>
              Receipts and invoices will be sent to {email}
            </MetaListItem>
            <MetaListItem>
              <Button variant="link" onClick={showEmailModal}>
                Change
              </Button>
            </MetaListItem>
          </MetaList>
        </div>
      </SettingsSection>

      <SettingsSection header="Danger zone">
        <Cards>
          <SettingCard
            actions={
              <Button variant="secondary" danger onClick={cancelBilling}>
                Cancel subscription
              </Button>
            }
            title="Cancel subscription"
            details={`It will remain active until ${moment(periodEndAt).format(
              'LL',
            )}`}
          />
        </Cards>
      </SettingsSection>
    </>
  );
};

BillingStatusView.fragments = {
  billingSummary: BILLING_SUMMARY_FRAGMENT,
  billingPricing: BILLING_PRICING_FRAGMENT,
};

export default BillingStatusView;
